'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';

interface NewBurialForm {
  deceasedFullName: string;
  deceasedIdNumber: string;
  dateOfBirth: string;
  dateOfDeath: string;
  causeOfDeath: string;
  cemetery: string;
  applicantName: string;
  applicantIdNumber: string;
  relationship: string;
  applicantPhone: string;
  applicantEmail: string;
  applicantAddress: string;
}

export default function NewBurialModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewBurialForm>();

  const onSubmit = async (data: NewBurialForm) => {
    setLoading(true);
    // TODO: BACKEND — POST /api/burial-applications
    await new Promise((r) => setTimeout(r, 1400));
    setLoading(false);
    toast.success('Burial application submitted — APP-2026-0853 created');
    onClose();
  };

  const steps = ['Deceased Details', 'Next of Kin', 'Cemetery & Plot'];

  return (
    <Modal open onClose={onClose} title="New Burial Application" subtitle="Complete all three steps to submit" size="lg">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-6">
        {steps.map((s, i) => (
          <React.Fragment key={`step-${i + 1}`}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 transition-colors ${
                step > i + 1 ? 'bg-positive text-white' : step === i + 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <span className={`text-xs font-500 whitespace-nowrap ${step === i + 1 ? 'text-primary' : 'text-muted-foreground'}`}>{s}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px mx-2 ${step > i + 1 ? 'bg-positive' : 'bg-border'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label-base">Full Name of Deceased <span className="text-negative">*</span></label>
              <input {...register('deceasedFullName', { required: 'Full name is required' })} className="input-base" placeholder="e.g. Nokwanda Zulu" />
              {errors.deceasedFullName && <p className="text-xs text-negative mt-1">{errors.deceasedFullName.message}</p>}
            </div>
            <div>
              <label className="label-base">SA ID Number <span className="text-negative">*</span></label>
              <p className="text-xs text-muted-foreground mb-1">13-digit South African ID</p>
              <input {...register('deceasedIdNumber', { required: 'ID number is required', minLength: { value: 13, message: 'Must be 13 digits' } })} className="input-base font-mono-data" placeholder="0000000000000" maxLength={13} />
              {errors.deceasedIdNumber && <p className="text-xs text-negative mt-1">{errors.deceasedIdNumber.message}</p>}
            </div>
            <div>
              <label className="label-base">Date of Birth</label>
              <input type="date" {...register('dateOfBirth')} className="input-base" />
            </div>
            <div>
              <label className="label-base">Date of Death <span className="text-negative">*</span></label>
              <input type="date" {...register('dateOfDeath', { required: 'Date of death is required' })} className="input-base" />
              {errors.dateOfDeath && <p className="text-xs text-negative mt-1">{errors.dateOfDeath.message}</p>}
            </div>
            <div>
              <label className="label-base">Cause of Death</label>
              <input {...register('causeOfDeath')} className="input-base" placeholder="e.g. Natural causes" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label-base">Applicant Full Name <span className="text-negative">*</span></label>
              <input {...register('applicantName', { required: 'Applicant name is required' })} className="input-base" />
              {errors.applicantName && <p className="text-xs text-negative mt-1">{errors.applicantName.message}</p>}
            </div>
            <div>
              <label className="label-base">Applicant ID Number <span className="text-negative">*</span></label>
              <input {...register('applicantIdNumber', { required: 'Required' })} className="input-base font-mono-data" maxLength={13} />
              {errors.applicantIdNumber && <p className="text-xs text-negative mt-1">{errors.applicantIdNumber.message}</p>}
            </div>
            <div>
              <label className="label-base">Relationship to Deceased <span className="text-negative">*</span></label>
              <select {...register('relationship', { required: 'Required' })} className="input-base">
                <option value="">Select relationship</option>
                {['Spouse', 'Son', 'Daughter', 'Father', 'Mother', 'Sibling', 'Grandchild', 'Other'].map((r) => (
                  <option key={`rel-${r}`} value={r}>{r}</option>
                ))}
              </select>
              {errors.relationship && <p className="text-xs text-negative mt-1">{errors.relationship.message}</p>}
            </div>
            <div>
              <label className="label-base">Phone Number <span className="text-negative">*</span></label>
              <input type="tel" {...register('applicantPhone', { required: 'Phone is required' })} className="input-base" placeholder="+27 00 000 0000" />
              {errors.applicantPhone && <p className="text-xs text-negative mt-1">{errors.applicantPhone.message}</p>}
            </div>
            <div>
              <label className="label-base">Email Address</label>
              <input type="email" {...register('applicantEmail')} className="input-base" />
            </div>
            <div className="md:col-span-2">
              <label className="label-base">Residential Address</label>
              <textarea {...register('applicantAddress')} rows={2} className="input-base resize-none" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label-base">Cemetery <span className="text-negative">*</span></label>
              <select {...register('cemetery', { required: 'Cemetery is required' })} className="input-base">
                <option value="">Select cemetery</option>
                {['Mthatha Cemetery', 'Libode Cemetery', 'Ngqeleni Cemetery'].map((c) => (
                  <option key={`cem-opt-${c}`} value={c}>{c}</option>
                ))}
              </select>
              {errors.cemetery && <p className="text-xs text-negative mt-1">{errors.cemetery.message}</p>}
            </div>
            <div className="md:col-span-2 p-4 bg-secondary rounded-lg border border-primary/20">
              <p className="text-sm font-600 text-primary mb-1">Plot Assignment</p>
              <p className="text-xs text-muted-foreground">
                Plot assignment will be done by the cemetery officer after application approval. Available plots will be shown based on the selected cemetery.
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="label-base">Upload Burial Permit</label>
              <p className="text-xs text-muted-foreground mb-2">PDF or image — max 5MB</p>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/40 transition-colors cursor-pointer">
                <p className="text-sm text-muted-foreground">Click to upload or drag & drop</p>
                <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG accepted</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="btn-ghost"
          >
            {step > 1 ? 'Back' : 'Cancel'}
          </button>
          {step < 3 ? (
            <button type="button" onClick={() => setStep(step + 1)} className="btn-primary">
              Continue
            </button>
          ) : (
            <button type="submit" disabled={loading} className="btn-primary min-w-[140px] justify-center">
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : 'Submit Application'}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}